// PlanFinancementBlock — section PROPRE du split Conformité (pièce du dépôt ANAH).
// Affiche le résumé chiffré du plan + un état de génération PERSISTANT :
//   - détecte au montage si un PDF « plan_financement » a déjà été généré
//     (GET /devis-contrats, source unique des docs générés — O1),
//   - si oui : « Plan de financement généré le <date> » + bouton TÉLÉCHARGER
//     (persistant, ne disparaît plus au changement d'écran) + bouton RÉGÉNÉRER
//     (séparé, optionnel — plus de régénération forcée),
//   - sinon : bouton « Générer le PDF ».
// L'édition du plan (CERFA) reste dans la modale FinancementTab via onEdit.
import { useState, useEffect, useCallback } from "react";
import { Calculator, Download, RefreshCw, FileText, PenTool } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { API, getAuthHeaders, formatDate } from "./helpers";
import { toast } from "sonner";

export function PlanFinancementBlock({ dossier, dossierId, tenantId, onEdit }) {
  const [genDoc, setGenDoc] = useState(null);   // doc plan_financement déjà généré (ou null)
  const [planData, setPlanData] = useState({}); // données du plan (endpoint /financement)
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Charge (1) les données du plan via l'endpoint canonique /financement — le même
  // que la modale FinancementTab (O1 ; `dossier.plan_financement` n'est PAS exposé
  // par l'API), et (2) le doc PDF déjà généré via /devis-contrats (source unique
  // des docs générés) pour l'état de téléchargement persistant.
  const loadGenerated = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const [planRes, docsRes] = await Promise.all([
        fetch(`${API}/api/metier/tef/dossiers/${dossierId}/financement`, { headers: getAuthHeaders(tenantId), credentials: "include" }),
        fetch(`${API}/api/metier/tef/dossiers/${dossierId}/devis-contrats`, { headers: getAuthHeaders(tenantId), credentials: "include" }),
      ]);
      if (planRes.ok) { const d = await planRes.json(); setPlanData(d.plan_financement || {}); }
      if (docsRes.ok) { const d = await docsRes.json(); setGenDoc((d.documents || []).find(x => x.type_document === "plan_financement") || null); }
    } catch { /* silencieux : le bouton Générer reste proposé */ }
    finally { setLoading(false); }
  }, [dossierId, tenantId]);

  useEffect(() => { loadGenerated(); }, [loadGenerated]);

  const pf = planData || {};
  const hasPlan = !!(pf.montant_travaux_ttc || pf.aide_anah || pf.aide_mpa);
  const eur = (v) => v != null ? `${Number(v).toLocaleString("fr-FR")} €` : "—";
  const aides = (pf.aide_anah || 0) + (pf.aide_mpa || 0) + (pf.aide_commune || 0) + (pf.aide_departement || 0) + (pf.aide_region || 0) + (pf.aide_caisse_retraite || 0) + (pf.autres_aides || 0);
  const equilibre = pf.reste_a_charge ?? (pf.montant_travaux_ttc != null ? (pf.montant_travaux_ttc - aides) : null);

  const handleDownload = async () => {
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/plan-financement/download`, { headers: getAuthHeaders(tenantId), credentials: "include" });
      if (!res.ok) { toast.error("Téléchargement impossible"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = genDoc?.pdf_filename || genDoc?.nom || `Plan_Financement_${dossier?.reference || dossierId}.pdf`;
      document.body.appendChild(a); a.click();
      URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch { toast.error("Erreur réseau"); }
  };

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/financement/generer-pdf`, {
        method: "POST", headers: getAuthHeaders(tenantId, "application/json"), body: JSON.stringify({}), credentials: "include",
      });
      if (res.ok) { toast.success(genDoc ? "Plan régénéré" : "Plan généré"); await loadGenerated(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Échec de la génération"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setBusy(false); }
  };

  return (
    <div data-subnav-id="plan-financement" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="bloc-plan-financement">
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--oh-border)" }}>
        <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <Calculator className="w-4 h-4" style={{ color: "var(--oh-stat-purple-text)" }} /> Plan de financement MPA <span className="text-[11px] font-normal" style={{ color: "var(--oh-text-muted)" }}>(CERFA 13460-03)</span>
        </span>
        <Badge className="text-[10px] border-0" style={hasPlan ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" } : { background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>{hasPlan ? "Plan établi" : "Aucun plan"}</Badge>
      </div>

      <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--oh-border)" }}>
        <span className="text-xs" style={{ color: "var(--oh-text-muted)" }} data-testid="plan-resume">
          Dépenses TTC {eur(pf.montant_travaux_ttc)} · Total aides {aides ? eur(aides) : "—"} · Équilibre <b style={{ color: "var(--oh-text)" }}>{eur(equilibre)}</b>
        </span>
      </div>

      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs flex items-center gap-2" style={{ color: "var(--oh-text-muted)" }} data-testid="plan-gen-status">
          {loading ? (
            <span>Vérification du document…</span>
          ) : genDoc ? (
            <span className="flex items-center gap-1.5" style={{ color: "var(--oh-stat-green-text)" }}>
              <FileText className="w-3.5 h-3.5" /> Plan de financement généré le {formatDate(genDoc.created_at)}
            </span>
          ) : (
            <span>Aucun document généré pour l'instant.</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {genDoc && (
            <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={handleDownload} data-testid="btn-download-plan">
              <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
            </Button>
          )}
          {hasPlan && (
            <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={handleGenerate} disabled={busy} data-testid="btn-regenerate-plan">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> {genDoc ? "Régénérer" : "Générer le PDF"}
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 px-3 text-xs" style={{ color: "var(--oh-stat-blue-text)" }} onClick={onEdit} data-testid="btn-open-plan-modal">
            <PenTool className="w-3.5 h-3.5 mr-1" /> Créer / Modifier ↗
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlanFinancementBlock;
